import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Container, Grid, Typography, List, Anchor } from 'components/base';
import Icon, { ICON_TYPE } from 'components/icon';
import BaseFooter from 'components/layout/footer';

import SETTINGS from '../../settings';
import {
  ShippingPageLink,
  AboutPageLink,
  TermsPageLink,
  PrivacyPageLink,
  ReferFriendPageLink,
} from '../pageLinks';

// Paths where the footer renders in the dark (CTA) variant. Default everywhere else is light.
const DARK_FOOTER_PATHS = ['/about', '/support', '/ancestry', '/ancestry-book-pages'];

const FOOTER_VARIANT = {
  LIGHT: 'light',
  DARK: 'dark',
};

const Footer = ({ variant }) => {
  const router = useRouter();
  const resolvedVariant =
    variant || (DARK_FOOTER_PATHS.includes(router?.pathname) ? FOOTER_VARIANT.DARK : FOOTER_VARIANT.LIGHT);

  const className = classNames('footer-mycanvas', {
    'footer-mycanvas-dark': resolvedVariant === FOOTER_VARIANT.DARK,
  });

  return (
    <BaseFooter className={className}>
      <Container>
        <Grid.Row>
          <Grid.Col xs={12} md={8} className="mb-2">
            <Typography.H5>Stay In Touch</Typography.H5>
            <Typography.P className="text-light mb-3">
              Follow along for all things MyCanvas.
            </Typography.P>
            <List className="list-social">
              <List.Item className="list-social-item">
                <Anchor
                  href={SETTINGS.YOUTUBE_URL}
                  className="list-social-link"
                  targetBlank
                >
                  <Icon type={ICON_TYPE.YOUTUBE} />
                </Anchor>
              </List.Item>
              <List.Item className="list-social-item">
                <Anchor
                  href={SETTINGS.X_URL}
                  className="list-social-link"
                  targetBlank
                >
                  <Icon type={ICON_TYPE.TWITTER} />
                </Anchor>
              </List.Item>
              <List.Item className="list-social-item">
                <Anchor
                  href={SETTINGS.FACEBOOK_URL}
                  className="list-social-link"
                  targetBlank
                >
                  <Icon type={ICON_TYPE.FACEBOOK} />
                </Anchor>
              </List.Item>
              <List.Item className="list-social-item">
                <Anchor
                  href={SETTINGS.INSTAGRAM_URL}
                  className="list-social-link"
                  targetBlank
                >
                  <Icon type={ICON_TYPE.INSTAGRAM} />
                </Anchor>
              </List.Item>
              <List.Item className="list-social-item">
                <Anchor
                  href={SETTINGS.PINTEREST_URL}
                  className="list-social-link"
                  targetBlank
                >
                  <Icon type={ICON_TYPE.PINTEREST} />
                </Anchor>
              </List.Item>
            </List>

            <Typography.H5 className="mt-1">Love MyCanvas? Share With Friends!</Typography.H5>
            <List className="list-footer">
              <List.Item className="list-footer-item">
                <ReferFriendPageLink>
                  <Anchor className="list-footer-link">Give $10, Get $10</Anchor>
                </ReferFriendPageLink>
              </List.Item>
            </List>
          </Grid.Col>

          <Grid.Col xs={6} md={2} className="mb-2">
            <Typography.H5>Company</Typography.H5>
            <List className="list-footer">
              <List.Item className="list-footer-item">
                <AboutPageLink>
                  <Anchor className="list-footer-link">About MyCanvas</Anchor>
                </AboutPageLink>
              </List.Item>
              <List.Item className="list-footer-item">
                <Anchor href="/blog/" className="list-footer-link">
                  Our Blog
                </Anchor>
              </List.Item>
              <List.Item className="list-footer-item">
                <Anchor href={SETTINGS.NEWSLETTER_URL} targetBlank className="list-footer-link">
                  Newsletter
                </Anchor>
              </List.Item>
              <List.Item className="list-footer-item">
                <PrivacyPageLink>
                  <Anchor className="list-footer-link">Privacy Policy</Anchor>
                </PrivacyPageLink>
              </List.Item>
              <List.Item className="list-footer-item">
                <TermsPageLink>
                  <Anchor className="list-footer-link">Terms of Use</Anchor>
                </TermsPageLink>
              </List.Item>
            </List>
          </Grid.Col>

          <Grid.Col xs={6} md={2} className="mb-2">
            <Typography.H5>Questions?</Typography.H5>
            <List className="list-footer">
              <List.Item className="list-footer-item">
                <ShippingPageLink>
                  <Anchor className="list-footer-link">Shipping and Returns</Anchor>
                </ShippingPageLink>
              </List.Item>
              <List.Item className="list-footer-item">
                <Anchor href={SETTINGS.HELPDESK_URL} targetBlank className="list-footer-link">
                  Check out our FAQ
                </Anchor>
              </List.Item>
              <List.Item className="list-footer-item">
                <Anchor href={SETTINGS.STATUS_URL} targetBlank className="list-footer-link">
                  Site status
                </Anchor>
              </List.Item>
              <List.Item className="list-footer-item">
                <Anchor href="mailto:help@mycanvas.com" className="list-footer-link">
                  Email us at
                  <br />
                  help@mycanvas.com
                </Anchor>
              </List.Item>
            </List>
          </Grid.Col>
        </Grid.Row>

        <Typography.P className="text-light mt-3">{SETTINGS.APP_COPY}</Typography.P>
      </Container>
    </BaseFooter>
  );
};

Footer.propTypes = {
  variant: PropTypes.oneOf(Object.values(FOOTER_VARIANT)),
};

export default Footer;
